import { useEffect, useState } from 'react';

type AmazonPayInit = {
  checkoutLanguage: string;
  ledgerCurrency: string;
  merchantId: string;
  productType: string;
  sandbox: string;
  sessionId: string;
};

type AmazonPayBindActions = {
  elementIds: string[];
};

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    amazon: any;
  }
}

const loadAmazonSdk = () =>
  new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://static-eu.payments-amazon.com/checkout.js';
    script.id = 'amazon-pay-sdk';
    script.onload = () => {
      resolve(true);
    };
    script.onerror = error => {
      reject(error);
    };
    document.getElementsByTagName('head')[0].appendChild(script);
  });

export const initializeSessionPayload = {
  key: 'amazon_pay',
  status: { isSelected: true },
  configuration: {
    initialization: {
      initializeSession: true,
    },
  },
};

export const redirectToAmazon = async ({
  checkoutLanguage,
  ledgerCurrency,
  merchantId,
  productType,
  sandbox,
  sessionId,
}: AmazonPayInit) => {
  if (typeof window.amazon === 'undefined') {
    await loadAmazonSdk();
  }
  window.amazon.Pay.initCheckout({
    checkoutLanguage,
    createCheckoutSession: async () => sessionId,
    ledgerCurrency,
    merchantId,
    placement: 'Other',
    productType,
    sandbox,
  });
};

export const bindAmazonPayLinks = async ({ elementIds }: AmazonPayBindActions) => {
  if (typeof window.amazon === 'undefined') {
    await loadAmazonSdk();
  }
  const queryParams = new URLSearchParams(window.location.search);
  const amazonCheckoutSessionId = queryParams.get('amazonCheckoutSessionId');
  if (amazonCheckoutSessionId) {
    elementIds.forEach(elementId =>
      window.amazon.Pay.bindChangeAction(elementId, {
        amazonCheckoutSessionId,
        changeAction: 'changeAddress',
      })
    );
  }
};

const useAmazonPay = (elementIds: string[]) => {
  const [isAmazonSdkLoaded, setIsAmazonSdkLoaded] = useState(false);
  useEffect(() => {
    const amazonSdk = document.getElementById('amazon-pay-sdk');
    setIsAmazonSdkLoaded(!!amazonSdk);

    const bindLinks = async () => {
      if (amazonSdk) {
        await bindAmazonPayLinks({ elementIds });
        setIsAmazonSdkLoaded(true);
      }
    };

    bindLinks();
  }, [elementIds]);
  return { isAmazonSdkLoaded, setIsAmazonSdkLoaded };
};

export default useAmazonPay;
